import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { API_BASE } from "../../../../utils/api";

/**
 * Custom hook for managing category data and operations
 * Handles fetching, editing, and updating categories
 */
export const useCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editOtherLang, setEditOtherLang] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("en");
  const [translations, setTranslations] = useState({});
  const [printerCategories, setPrinterCategories] = useState([]);
  const [printKey, setPrintKey] = useState("kitchen");
  const [isVisible, setIsVisible] = useState(true);

  const itemsPerPage = 40;
  const ENABLE_PRINT_CAT = process.env.REACT_APP_PRINT_CAT === "true";

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/categories/all`);
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();

      const normalized = data.map((cat) => ({
        ...cat,
        _i18n: {
          name: cat.name || {},
          description: cat.description || {},
        },
        name: typeof cat.name === "string" ? cat.name : cat.name?.en || "",
        description:
          typeof cat.description === "string"
            ? cat.description
            : cat.description?.en || "",
      }));

      setCategories(normalized);
      setTotalPages(Math.ceil(normalized.length / itemsPerPage));
    } catch (err) {
      setError(err.message);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  // Fetch languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/languages/availableLanguages`);
        const data = await res.json();
        setLanguages(data);
      } catch (err) {
        console.error("Failed to load languages", err);
      }
    };

    fetchLanguages();
  }, []);

  // Fetch printer categories
  useEffect(() => {
    if (!ENABLE_PRINT_CAT) return;

    fetch(`${API_BASE}/api/restaurant/loadPrintCatagories`)
      .then((res) => res.json())
      .then((data) => setPrinterCategories(data))
      .catch(() => toast.error("Failed to load printer categories"));
  }, [ENABLE_PRINT_CAT]);

  // Initial fetch
  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle category selection for editing
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);

    // Initialize translations
    const initialTranslations = {};
    languages.forEach((lang) => {
      initialTranslations[lang.code] = {
        name: category._i18n?.name?.[lang.code] || "",
        description: category._i18n?.description?.[lang.code] || "",
      };
    });

    initialTranslations.en = {
      name: category.name || "",
      description: category.description || "",
    };

    setTranslations(initialTranslations);
    setSelectedLang("en");
    setEditOtherLang(false);
    setNewImage(null);
    setPrintKey(category.printKey || "kitchen");
    setIsVisible(!!category.isVisible);

    setTimeout(() => {
      setOpenModal(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  // Handle category update
  const handleUpdateCategory = async () => {
    const formData = new FormData();

    // Send all language translations
    formData.append(
      "name",
      JSON.stringify(
        Object.fromEntries(
          Object.entries(translations).map(([lang, val]) => [
            lang,
            val.name || "",
          ]),
        ),
      ),
    );

    formData.append(
      "description",
      JSON.stringify(
        Object.fromEntries(
          Object.entries(translations).map(([lang, val]) => [
            lang,
            val.description || "",
          ]),
        ),
      ),
    );

    formData.append("printKey", printKey || "kitchen");
    formData.append("isVisible", String(Boolean(isVisible)));

    if (newImage) {
      formData.append("image", newImage);
    }

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory._id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update category");
      }

      await fetchCategories();
      toast.success("Category updated successfully!");
      setOpenModal(false);
    } catch (err) {
      console.error("Error updating category:", err);
      toast.error(err.message || "Failed to update category.");
    }
  };

  // Get filtered categories
  const getFilteredCategories = (searchTerm = "") => {
    return categories.filter((category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  // Get paginated categories
  const getPaginatedCategories = (filtered, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  return {
    categories,
    loading,
    error,
    openModal,
    setOpenModal,
    selectedCategory,
    newImage,
    setNewImage,
    currentPage,
    setCurrentPage,
    totalPages,
    editOtherLang,
    setEditOtherLang,
    languages,
    selectedLang,
    setSelectedLang,
    translations,
    setTranslations,
    printerCategories,
    ENABLE_PRINT_CAT,
    itemsPerPage,
    printKey,
    setPrintKey,
    isVisible,
    setIsVisible,
    handleCategorySelect,
    handleUpdateCategory,
    getFilteredCategories,
    getPaginatedCategories,
  };
};
